// Pure view-model for RunDetail's Root cause tab (unit-tested in rootcauseModel.test.js).
// Input: the run detail (`getRun`) whose `payload.rootcause_json` is the server's root-cause result
// (contract: server `app/services/rootcause/service.py` docstring). Only MISSED rubric items are analyzed;
// captured items are only counted. INTERNAL data (guard expressions) — never exported.
//
// Card v2 (wireframe APPROVED 2026-10-21, PLAN §2): each card shows ONLY the item, a label (category +
// reason), "Failed:" (what failed), "Why:" (one server-built sentence) and "View in transcript".
// v2 results (`version: 2`) carry `evidence.failed_conditions`, `evidence.emulator_answer` and `why`;
// v1 results fall back to the label + a short Failed line built from the older evidence.
// v3 (card polish, 2026-10-22) adds `failed_conditions[].label` (readable condition — the main line; the raw
// expression stays available, collapsed) and `*_display` answers (MCQ ids → option labels, resolved by the
// server from the graph). Without them (v1/v2) the raw expression is the main line and answers resolve
// from the transcript's mcq_options, as before.
import { matchAnswerToOptions } from "./components/TranscriptMCQ";

export const CATEGORY_DISPLAY = {
  not_in_graph: "Not in graph",
  not_asked: "Not asked",
  related_question_only: "Only a related question asked",
  asked_wrong_fact: "Asked, wrong or no fact recorded",
  asked_not_in_note: "Asked, not in note",
  rubric_case_conflict: "Rubric and case disagree",
  emulator_error: "Patient emulator error",
};
export const REASON_DISPLAY = {
  branch_not_taken: "Branch not taken",
  skipped_already_answered: "Skipped (already answered)",
  not_reached: "Not reached",
  contradicts_case: "Contradicts case",
  evasive_answer: "Evasive answer",
};
export const SECTIONS = [["redflags", "Red flags"], ["must_capture", "Must capture"]];
export const UNSUPPORTED_MSG = "Root-cause analysis is available only for runs made after 2026-10-20.";
export const ERROR_MSG = "Root-cause analysis failed.";
export const WAITING_MSG = "Root-cause analysis starts after the run is graded.";
export const POLL_MS = 3000;

const UNSET = "<unset>";
const TERMINAL = new Set(["complete", "partial", "error", "unsupported"]);

// Guard expressions read better without the `facts.` root (keys stay in dot notation).
export const stripFacts = (expr) => String(expr || "").replace(/\bfacts\./g, "");

// v1 fact values (no server display string).
export const fmtPatientValue = (v) => {
  if (v === UNSET || v == null) return "not recorded";
  if (v === true) return "yes";
  if (v === false) return "no";
  if (Array.isArray(v)) return v.map(fmtPatientValue).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v).replace(/_/g, " ");
};

// Transcript index of a question → the matching buildQAPairs index (pairs carry qIndex/aIndex).
export function qaPairIndex(pairs, tidx) {
  if (!Number.isInteger(tidx) || tidx < 0) return -1;
  return (pairs || []).findIndex((p) => p.qIndex === tidx || p.aIndex === tidx);
}

// The transcript index of the question an evidence block points at. `transcript_turn` is exact (the
// question's index). Otherwise `turn` = the loop turn whose patient message answered the question at
// transcript[2*(turn-1)] (server context: visit V = transcript[2V], answered at turn V+1). For a skipped
// node, `turn` is the walk's turn — that answer is where the branch was decided.
export function evidenceTranscriptIndex(ev) {
  if (!ev) return null;
  if (Number.isInteger(ev.transcript_turn) && ev.transcript_turn >= 0) return ev.transcript_turn;
  if (Number.isInteger(ev.turn) && ev.turn >= 1) return 2 * (ev.turn - 1);
  return null;
}

// Patient answer → selected MCQ option labels when the question at `tidx` carries mcq_options and the
// reply matches them; else the raw reply text.
export function answerDisplay(answer, transcript, tidx) {
  if (answer == null || answer === "") return null;
  const q = Number.isInteger(tidx) ? (transcript || [])[tidx] : null;
  if (q && q.role === "system" && Array.isArray(q.mcq_options)) {
    const ids = matchAnswerToOptions(q.mcq_options, q.mcq_selection, answer);
    if (ids.length) return q.mcq_options.filter((o) => ids.includes(o.id)).map((o) => o.label).join("; ");
  }
  return String(answer);
}

// A failed condition: `label` = readable main line (v3; null before), `expression` = the raw guard (shown
// as the main line when there is no label, else collapsed under it).
const conditionLine = (expression, patient, label = null) => ({
  kind: "condition", label: label || null, expression: expression ? stripFacts(expression) : null, patient,
});

// Server-resolved answer labels (v3 `*_display`), else the transcript-based resolution.
const answerOf = (display, raw, transcript, tidx) => (display != null && display !== "" ? String(display) : answerDisplay(raw, transcript, tidx));

// What the patient's recorded facts say (only the selected / non-false values; checklists record every
// option, mostly "no").
function recordedText(facts) {
  const fs = facts || [];
  if (!fs.length) return "nothing recorded";
  const shown = fs.filter((f) => f.value !== false);
  if (!shown.length) return "no to every option";
  return shown.map((f) => `${f.key} = ${fmtPatientValue(f.value)}`).join(", ");
}

// "Failed:" lines. Each: {kind: "condition", expression, patient} | {kind: "text", text} |
// {kind: "qa", question, answer, recorded?} | {kind: "emulator", question, answer, caseText}.
export function failedLines(it, transcript) {
  const ev = it.evidence || {};
  const cat = it.category;
  if (it.error) return [{ kind: "text", text: "Not analyzed: the question mapping was unavailable." }];
  if (cat === "emulator_error" && ev.emulator_answer) {
    const em = ev.emulator_answer;
    return [{ kind: "emulator", question: em.question || null,
              answer: answerOf(em.patient_answer_display, em.patient_answer, transcript, em.transcript_turn) || "(no answer)",
              caseText: em.case_evidence || null }];
  }
  if (cat === "rubric_case_conflict") return [{ kind: "text", text: "The case file does not mention this." }];
  if (cat === "not_in_graph") return [{ kind: "text", text: "No question covers this." }];
  if (Array.isArray(ev.failed_conditions) && ev.failed_conditions.length) {
    // `expression` null = a facts-already-set skip (label "… already answered").
    return ev.failed_conditions.map((c) => conditionLine(
      c.expression,
      (c.patient_values || []).map((v) => v.display ?? fmtPatientValue(v.value)).join(", ") || null,
      c.label || (c.expression ? null : "already answered"),
    ));
  }
  const qa = ev.question || ev.patient_answer != null
    ? { kind: "qa", question: ev.question || null,
        answer: answerOf(ev.patient_answer_display, ev.patient_answer, transcript, ev.transcript_turn) } : null;
  if (cat === "related_question_only" && qa) return [qa];
  if ((cat === "asked_wrong_fact" || cat === "asked_not_in_note") && qa) return [{ ...qa, recorded: recordedText(ev.captured_facts) }];
  // v1 fallback for a blocked gate: the whole guard with the values the runtime evaluated.
  if (ev.failing_guard && ev.failing_guard.expression) {
    const vals = Object.values(ev.fact_values || {}).map(fmtPatientValue).join(", ");
    return [conditionLine(ev.failing_guard.expression, vals || null)];
  }
  if (cat === "not_asked" && it.reason === "not_reached") return [{ kind: "text", text: "The question was not reached." }];
  return qa ? [qa] : [];
}

export function cardView(it, i, transcript) {
  const code = it.category || null;
  const ev = it.evidence || {};
  const em = ev.emulator_answer;
  const tidx = em && Number.isInteger(em.transcript_turn) ? em.transcript_turn : evidenceTranscriptIndex(ev);
  return {
    key: `${it.rubric}:${i}:${it.item}`,
    item: it.item,
    rubric: it.rubric,
    categoryLabel: code ? (it.display_name || CATEGORY_DISPLAY[code] || code) : "Not analyzed",
    reasonLabel: it.reason ? (it.reason_display || REASON_DISPLAY[it.reason] || it.reason) : null,
    failed: failedLines(it, transcript),
    why: it.why || null,
    transcriptIndex: tidx,
  };
}

export function bannerText(b) {
  if (!b || b.code !== "different_graph_chosen") return null;
  return `${b.display_name || "Different graph chosen"}: Mari ran ${b.classified_cc || "another graph"}; the case complaint is ${b.case_chief_complaint || "unknown"}`;
}

// What a partial result lacks, from the server's error strings.
export function partialNote(rc) {
  if (rc?.status !== "partial") return null;
  const parts = [];
  const add = (s) => { if (!parts.includes(s)) parts.push(s); };
  for (const e of rc.errors || []) {
    const s = String(e);
    if (/^mapp(ing|er)/i.test(s)) add("question mapping");
    else if (/^emulator check/i.test(s)) add("emulator and case checks");
    else if (/debug\/evaluate|^engine/i.test(s)) add("engine checks");
    else add("some analysis steps");
  }
  if (!parts.length) add("some analysis steps");
  return `Partial result: ${parts.join(", ")} unavailable.`;
}

// Missed items grouped: Red flags, then Must capture (server order within a section).
export function sectionsView(items, transcript) {
  return SECTIONS.map(([rubric, title]) => {
    const cards = (items || []).filter((it) => it.rubric === rubric).map((it, i) => cardView(it, i, transcript));
    return { rubric, title: `${title} (${cards.length} missed)`, cards };
  }).filter((s) => s.cards.length);
}

// The whole tab. `state`:
//   hidden        — not a Mari run (tab not shown)
//   waiting       — Mari run, no result yet, not graded yet (poll)
//   pending       — analysis running (poll)
//   error         — analysis failed (message + Re-analyze)
//   unsupported   — run cannot be analyzed (old run, incomplete, …)
//   all_captured  — every item captured ("All N items captured.")
//   results       — missed-item sections (status complete or partial)
export function buildRootCauseView(run) {
  if (!run || run.system !== "mari") return { state: "hidden", showTab: false, poll: false };
  const p = run.payload || {};
  const rc = p.rootcause_json || null;
  const base = { showTab: true, poll: false, reanalyzeDisabled: false, header: null };
  if (!rc) {
    if (run.status === "complete" && !p.eval_json) return { ...base, state: "waiting", poll: true, message: WAITING_MSG };
    return { ...base, state: "unsupported", message: UNSUPPORTED_MSG, reason: run.status !== "complete" ? "run is not complete" : null };
  }
  const st = rc.status;
  if (st === "pending") return { ...base, state: "pending", poll: true, reanalyzeDisabled: true, message: "Analyzing…" };
  if (st === "error") return { ...base, state: "error", message: ERROR_MSG, reason: rc.reason || null };
  if (st === "unsupported" || !TERMINAL.has(st)) return { ...base, state: "unsupported", message: UNSUPPORTED_MSG, reason: rc.reason || null };

  const captured = rc.captured_count ?? 0;
  const missed = rc.missed_count ?? (rc.items || []).length;
  const total = captured + missed;
  const header = { captured, total, text: `${captured} of ${total} captured`, banner: bannerText(rc.banner) };
  const sections = sectionsView(rc.items, p.transcript || []);
  const common = { ...base, header, partialNote: partialNote(rc), sections };
  if (!sections.length && missed === 0) return { ...common, state: "all_captured", message: `All ${total} items captured.` };
  return { ...common, state: "results" };
}
